'use client'

import AnimatedDialog from '@trustoo/components/modal/AnimatedDialog'
import DialogCloseButton from '@trustoo/components/modal/DialogCloseButton'
import PlainDialog from '@trustoo/components/modal/PlainDialog'

/**
 * Dialog compound component.
 *
 * A light wrapper around the native `<dialog>` element, intended as a reusable building block
 * (and an eventual replacement for the legacy `Modal`). Follows SOLID principles:
 * - **Single Responsibility**: `Root` / `AnimatedRoot` own the dialog element/behaviour;
 *   `CloseButton` only closes
 * - **Open/Closed**: open for extension (compose any children, override styling via `className`)
 * - **Dependency Inversion**: children talk to the root through props, not globals
 *
 * Pick the root that fits the page: `Dialog.Root` is style-light with no animation (cheapest, for
 * high-traffic pages); `Dialog.AnimatedRoot` adds a scale + fade enter/exit. Both are style-light:
 * app-specific looks (wider, dark backdrop, full-screen on mobile, ...) are Tailwind classes passed
 * via `className` — see `@trustoo/components/modal/constants/dialogPresets`. Drive open/close state
 * with `useDialog`.
 *
 * Controlled via `open` + `onClose`: the close button (`onClick`), backdrop click and Escape all
 * call `onClose`; flip `open` in response.
 *
 * @example
 * ```tsx
 * import { clsx } from 'clsx'
 * import Dialog from '@trustoo/components/modal/Dialog'
 * import { useDialog } from '@trustoo/components/modal/hooks/useDialog'
 * import { DIALOG_BACKDROP_FADE, DIALOG_WIDTH_600 } from '@trustoo/components/modal/constants/dialogPresets'
 *
 * const { isOpen, open, close } = useDialog()
 *
 * // Static dialog; swap in `Dialog.AnimatedRoot` (same props) for a scale + fade enter/exit.
 * <button onClick={open}>Open</button>
 * <Dialog.Root open={isOpen} onClose={close} className={clsx(DIALOG_WIDTH_600, DIALOG_BACKDROP_FADE)}>
 *   <Dialog.CloseButton onClick={close} />
 *   <p>Dialog content</p>
 * </Dialog.Root>
 * ```
 */
const Dialog = {
    Root: PlainDialog,
    AnimatedRoot: AnimatedDialog,
    CloseButton: DialogCloseButton,
}

export default Dialog
